import React, { useEffect, useRef, useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ROLE_LABELS } from '../../constants/roles';
import AppLogo from '../common/AppLogo';
// Idle 15-min session logout — restore when re-enabled
// import SessionTimeoutManager from '../auth/SessionTimeoutManager';
import NotificationBell from '../notifications/NotificationBell';
import bridgeService from '../../services/bridgeService';
import { filterBridgesByRole } from '../../utils/bridgeAccess';
import { getStaffName } from '../../utils/bridgeStaffDisplay';
import { handleFieldArrowNavigation } from '../../utils/fieldArrowNavigation';
import './Layout.css';

// Super Admin reaches Dashboard / Assets / Sensor Data via company bridge access,
// so those sidebar items are hidden for that role only. To show them again for
// Super Admin, restore `roles: null` on the three items below.
const NON_SUPER_ADMIN_NAV_ROLES = [
  'bridge_owner',
  'bridge_admin',
  'bridge_manager',
  'bridge_engineer',
  'project_manager',
  'project_engineer',
];

const DASHBOARD_NAV_ROLES = NON_SUPER_ADMIN_NAV_ROLES.filter((role) => role !== 'bridge_engineer');

const NAV_ITEMS = [
  { path: '/companies',   icon: 'ti-building',         label: 'Companies',    roles: ['super_admin'] },
  // { path: '/dashboard',   icon: 'ti-layout-dashboard', label: 'Dashboard',    roles: null },
  // { path: '/bridges',     icon: 'ti-barrier-block',    label: 'Assets Map/List',  roles: null },
  // { path: '/sensor-data', icon: 'ti-radio',            label: 'Sensor Data',  roles: null },
  { path: '/dashboard',   icon: 'ti-layout-dashboard', label: 'Dashboard',    roles: DASHBOARD_NAV_ROLES },
  { path: '/bridges',     icon: 'ti-barrier-block',    label: 'Assets Map/List',  roles: NON_SUPER_ADMIN_NAV_ROLES },
  { path: '/sensor-data', icon: 'ti-radio',            label: 'Sensor Data',  roles: NON_SUPER_ADMIN_NAV_ROLES },
];

const getUserInitials = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const normalizeId = (id) => {
  if (id == null || id === '') return '';
  if (typeof id === 'object') {
    if (id._id != null && id._id !== '') return String(id._id);
    if (id.id != null && id.id !== '') return String(id.id);
  }
  return String(id);
};

const managerMatchesUser = (manager, user) => {
  if (!manager || !user) return false;
  const userId = normalizeId(user._id ?? user.id);
  const managerUserId = normalizeId(manager.user);
  if (userId && managerUserId && userId === managerUserId) return true;
  const userEmail = (user.email || '').trim().toLowerCase();
  const managerEmail = (manager.email || '').trim().toLowerCase();
  return Boolean(userEmail && managerEmail && userEmail === managerEmail);
};

const collectManagerEngineers = (bridges, user) => {
  const seen = new Map();
  (bridges || []).forEach((bridge) => {
    (bridge.managers || []).forEach((manager) => {
      if (!managerMatchesUser(manager, user)) return;
      (manager.engineers || []).forEach((engineer) => {
        const name = getStaffName(engineer) || (engineer?.email || '').trim();
        if (!name) return;
        const key = (
          normalizeId(engineer.user)
          || (engineer.email || '').trim().toLowerCase()
          || name.toLowerCase()
        );
        if (!seen.has(key)) seen.set(key, { key, name });
      });
    });
  });
  return Array.from(seen.values());
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isBridgeManager = user?.role === 'bridge_manager';
  const [engineersOpen, setEngineersOpen] = useState(false);
  const [engineers, setEngineers] = useState([]);
  const [engineersLoading, setEngineersLoading] = useState(false);
  const [engineersError, setEngineersError] = useState('');
  const engineersRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    setEngineersOpen(false);
    setEngineers([]);
    setEngineersError('');
  }, [user?._id, user?.id, user?.role]);

  useEffect(() => {
    if (!engineersOpen) return undefined;
    const onDocPointerDown = (event) => {
      if (engineersRef.current?.contains(event.target)) return;
      setEngineersOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setEngineersOpen(false);
    };
    document.addEventListener('pointerdown', onDocPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onDocPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [engineersOpen]);

  const toggleEngineers = async () => {
    const nextOpen = !engineersOpen;
    setEngineersOpen(nextOpen);
    if (!nextOpen) return;
    setEngineersLoading(true);
    setEngineersError('');
    try {
      const data = await bridgeService.getAll();
      const list = filterBridgesByRole(data.bridges || [], user);
      setEngineers(collectManagerEngineers(list, user));
    } catch (e) {
      setEngineers([]);
      setEngineersError(e.message || 'Unable to load bridge engineers.');
    } finally {
      setEngineersLoading(false);
    }
  };

  return (
    <>
      {/* Idle 15-min session logout is disabled. Restore SessionTimeoutManager here. */}
      <div className="app-shell">
        {/* ── Sidebar ── */}
        <aside className="sidebar">
          <div className="sidebar-logo">
            <AppLogo variant="sidebar" />
          </div>
          <nav className="sidebar-nav">
            {NAV_ITEMS.filter(
              (item) => !item.roles || item.roles.includes(user?.role)
            ).map((item) =>
              item.disabled ? (
                <span
                  key={item.path}
                  className="nav-item disabled"
                  title="Report is currently unavailable"
                  aria-disabled="true"
                >
                  <i className={`ti ${item.icon}`} />
                  <span>{item.label}</span>
                </span>
              ) : (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                >
                  <i className={`ti ${item.icon}`} />
                  <span>{item.label}</span>
                </NavLink>
              )
            )}
          </nav>
        </aside>

        {/* ── Main area ── */}
        <div className="main-area">
          {/* Top bar */}
          <header className="topbar">
            <AppLogo variant="topbar" />
            <div className="topbar-right">
              <span className="role-badge">
                {ROLE_LABELS[user?.role] || user?.role}
              </span>

              {/* Alert bell */}
              <NotificationBell />

              <div className="topbar-user" ref={engineersRef}>
                <span className="topbar-avatar">{getUserInitials(user?.name)}</span>
                <span className="user-name">{user?.name}</span>
                {isBridgeManager && (
                  <button
                    type="button"
                    className="topbar-engineers-toggle"
                    aria-expanded={engineersOpen}
                    aria-label="Show bridge engineers"
                    onClick={toggleEngineers}
                  >
                    <i className={`ti ${engineersOpen ? 'ti-chevron-up' : 'ti-chevron-down'}`} />
                  </button>
                )}
                {isBridgeManager && engineersOpen && (
                  <div className="topbar-engineers-menu" role="menu">
                    <div className="topbar-engineers-title">Bridge Engineers</div>
                    {engineersLoading ? (
                      <div className="topbar-engineers-empty">Loading...</div>
                    ) : engineersError ? (
                      <div className="topbar-engineers-empty">{engineersError}</div>
                    ) : engineers.length === 0 ? (
                      <div className="topbar-engineers-empty">No bridge engineers</div>
                    ) : (
                      <ul className="topbar-engineers-list">
                        {engineers.map((engineer) => (
                          <li key={engineer.key} role="menuitem">
                            <span className="topbar-engineer-avatar">{getUserInitials(engineer.name)}</span>
                            <span>{engineer.name}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              <button className="btn-logout" onClick={handleLogout}>
                Log out
              </button>
            </div>
          </header>

          {/* Page content */}
          <main
            className="page-content"
            onKeyDown={(e) => handleFieldArrowNavigation(e, e.currentTarget)}
          >
            <Outlet />
          </main>
        </div>
      </div>
    </>
  );
}
